import React, { useEffect, useRef, useState } from "react";
import { GREEN_BOX_POINTS } from "../../pages/galaxy-alpr-dashboard.tsx";

interface WebcamTabProps {
    videoRef: React.RefObject<HTMLVideoElement | null>;
    canvasRef: React.RefObject<HTMLCanvasElement | null>;
    selectedCameraId: string | undefined;
    cameraDevices: MediaDeviceInfo[];
    setSelectedCameraId: (id: string) => void;
    selectedImage: string | null;
    updateImageStates: (dataUrl: string) => void;
    handleReset: () => void;
    selectedLocation: string;
    loadingLocations: boolean;
    locations: Array<{ id: number; name: string; type: string }>;
    setSelectedLocation: React.Dispatch<React.SetStateAction<string>>;
}

const WebcamTab: React.FC<WebcamTabProps> = ({
    videoRef,
    canvasRef,
    selectedCameraId,
    cameraDevices,
    setSelectedCameraId,
    selectedImage,
    updateImageStates,
    handleReset,
    selectedLocation,
    loadingLocations,
    locations,
    setSelectedLocation,
}) => {
    // State for detection area adjustment
    const [adjustablePoints, setAdjustablePoints] = useState<Array<{ x: number; y: number }>>(
        GREEN_BOX_POINTS
    );
    const [activePointIndex, setActivePointIndex] = useState<number | null>(null);
    const [showGreenbox, setShowGreenbox] = useState<boolean>(true);
    const [videoReady, setVideoReady] = useState<boolean>(false);

    // Use refs for values that need to be accessed in event handlers
    const svgRef = useRef<SVGSVGElement>(null);
    const isDraggingRef = useRef<boolean>(false);
    const activePointRef = useRef<number | null>(null);

    // Video dimensions for the SVG viewBox
    const video = videoRef.current;
    const previewWidth = video?.videoWidth || 600;
    const previewHeight = video?.videoHeight || 400;

    // Reset points when camera changes
    useEffect(() => {
        setAdjustablePoints([...GREEN_BOX_POINTS]);
    }, [selectedCameraId]);

    // Improved point dragging with refs for reliable event handling
    const handlePointMouseDown = (index: number) => (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();

        setActivePointIndex(index);
        activePointRef.current = index;
        isDraggingRef.current = true;

        // Add event listeners to window for better tracking
        window.addEventListener("mousemove", handleMouseMove);
        window.addEventListener("mouseup", handleMouseUp);
    };

    const handleMouseMove = (e: MouseEvent) => {
        if (!isDraggingRef.current || activePointRef.current === null || !svgRef.current) return;

        // Get the SVG bounding rectangle for coordinate calculation
        const rect = svgRef.current.getBoundingClientRect();

        // Calculate position relative to SVG
        const x = (e.clientX - rect.left) / rect.width;
        const y = (e.clientY - rect.top) / rect.height;

        // Constrain to valid range
        const boundedX = Math.max(0, Math.min(1, x));
        const boundedY = Math.max(0, Math.min(1, y));

        // Update the point position
        setAdjustablePoints((prevPoints) => {
            const newPoints = [...prevPoints];
            newPoints[activePointRef.current!] = { x: boundedX, y: boundedY };
            return newPoints;
        });
    };

    const handleMouseUp = (e: MouseEvent) => {
        if (!isDraggingRef.current) return;
        e.preventDefault();
        e.stopPropagation();

        // Clean up
        isDraggingRef.current = false;
        activePointRef.current = null;
        setActivePointIndex(null);

        // Remove event listeners
        window.removeEventListener("mousemove", handleMouseMove);
        window.removeEventListener("mouseup", handleMouseUp);
    };

    // Convert normalized points (0-1) to SVG points (based on video dimensions)
    const getScaledPoints = () => {
        return adjustablePoints.map((p) => ({
            x: p.x * previewWidth,
            y: p.y * previewHeight,
        }));
    };

    // Reset points to default
    const resetPoints = () => {
        setAdjustablePoints([...GREEN_BOX_POINTS]);
    };

    // Toggle green box visibility
    const toggleGreenbox = () => {
        setShowGreenbox((prev) => !prev);
    };

    // Crop image to the green box area
    const cropImageToGreenBox = async (video: HTMLVideoElement): Promise<string> => {
        return new Promise((resolve) => {
            const width = video.videoWidth;
            const height = video.videoHeight;
            const points = adjustablePoints.map((p) => ({
                x: p.x * width,
                y: p.y * height,
            }));

            const minX = Math.min(...points.map((p) => p.x));
            const maxX = Math.max(...points.map((p) => p.x));
            const minY = Math.min(...points.map((p) => p.y));
            const maxY = Math.max(...points.map((p) => p.y));
            const cropWidth = maxX - minX;
            const cropHeight = maxY - minY;

            const canvas = document.createElement("canvas");
            canvas.width = cropWidth;
            canvas.height = cropHeight;
            const ctx = canvas.getContext("2d");
            if (!ctx) return resolve("");

            ctx.save();
            ctx.beginPath();
            ctx.moveTo(points[0].x - minX, points[0].y - minY);
            for (let i = 1; i < points.length; i++) {
                ctx.lineTo(points[i].x - minX, points[i].y - minY);
            }
            ctx.closePath();
            ctx.clip();
            ctx.drawImage(
                video,
                minX,
                minY,
                cropWidth,
                cropHeight,
                0,
                0,
                cropWidth,
                cropHeight
            );
            ctx.restore();

            resolve(canvas.toDataURL("image/png"));
        });
    };

    // Capture photo from webcam
    const capturePhoto = async () => {
        const video = videoRef.current;
        if (!video) return;

        if (video.readyState >= 2) {
            // Create canvas and draw full frame
            const tempCanvas = document.createElement("canvas");
            tempCanvas.width = video.videoWidth;
            tempCanvas.height = video.videoHeight;
            const tempCtx = tempCanvas.getContext("2d");
            if (!tempCtx) return;

            tempCtx.drawImage(video, 0, 0, tempCanvas.width, tempCanvas.height);
            const originalDataUrl = tempCanvas.toDataURL("image/png");

            if (showGreenbox) {
                // Use our customized crop function with adjustable points
                const croppedDataUrl = await cropImageToGreenBox(video);
                updateImageStates(croppedDataUrl);
            } else {
                updateImageStates(originalDataUrl);
            }
        } else {
            alert("Camera not ready yet. Please wait a second and try again.");
        }
    };

    return (
        <div>
            {/* Location and Camera Dropdowns */}
            <div className="flex justify-center gap-4 mb-4">
                <select
                    className="px-4 py-2 rounded border border-gray-300 bg-white shadow"
                    value={selectedLocation}
                    onChange={(e) => setSelectedLocation(e.target.value)}
                    disabled={loadingLocations}
                >
                    {loadingLocations ? (
                        <option value="">Loading locations...</option>
                    ) : locations.length > 0 ? (
                        locations.map((loc) => (
                            <option key={loc.id} value={loc.name}>
                                {loc.name} ({loc.type})
                            </option>
                        ))
                    ) : (
                        <option value="">No locations available</option>
                    )}
                </select>

                <select
                    className="px-4 py-2 rounded border border-gray-300 bg-white shadow"
                    value={selectedCameraId}
                    onChange={(e) => setSelectedCameraId(e.target.value)}
                >
                    {cameraDevices.map((device) => (
                        <option key={device.deviceId} value={device.deviceId}>
                            {device.label || `Camera ${device.deviceId}`}
                        </option>
                    ))}
                </select>
            </div>

            {/* Green Box Toggle Switch */}
            <div className="flex justify-center mb-4">
                <label className="flex items-center gap-2 cursor-pointer select-none">
                    <span className="text-sm font-medium text-gray-700">
                        Enable Area Detection
                    </span>
                    <span className="relative inline-block w-10 align-middle select-none transition duration-200 ease-in">
                        <input
                            type="checkbox"
                            checked={showGreenbox}
                            onChange={toggleGreenbox}
                            className="sr-only"
                            disabled={!!selectedImage}
                        />
                        <span
                            className={`block w-10 h-6 rounded-full transition-colors ${showGreenbox ? "bg-green-500" : "bg-gray-300"
                                } ${!!selectedImage ? "opacity-50 cursor-not-allowed" : ""}`}
                        ></span>
                        <span
                            className={`dot absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform ${showGreenbox ? "translate-x-4" : ""
                                } ${!!selectedImage ? "opacity-50 cursor-not-allowed" : ""}`}
                            style={{ transition: "transform 0.2s" }}
                        ></span>
                    </span>
                </label>
            </div>

            {/* Camera View / Captured Image */}
            <div className="relative border-2 border-dashed border-gray-300 rounded-xl p-4 text-center">
                {!selectedImage ? (
                    <div className="relative flex justify-center items-center">
                        <video
                            ref={videoRef}
                            className="mx-auto rounded-xl max-h-none"
                            autoPlay
                            playsInline
                            onLoadedMetadata={() => {
                                setVideoReady(true);
                            }}
                        />
                        {/* SVG Overlay with improved interaction */}
                        {showGreenbox && previewWidth > 1 && previewHeight > 1 && (
                            <svg
                                ref={svgRef}
                                className="absolute top-0 left-0 w-full h-full pointer-events-auto"
                                viewBox={`0 0 ${previewWidth} ${previewHeight}`}
                                style={{
                                    maxWidth: "100%",
                                    maxHeight: "100%",
                                    zIndex: 10
                                }}
                            >
                                {/* Background to prevent click-through */}
                                <rect
                                    x="0" y="0"
                                    width={previewWidth}
                                    height={previewHeight}
                                    fill="transparent"
                                    pointerEvents="none"
                                />
                                {/* Area polygon */}
                                <polygon
                                    points={getScaledPoints().map((p) => `${p.x},${p.y}`).join(" ")}
                                    fill="rgba(34, 197, 94, 0.2)"
                                    stroke="#22c55e"
                                    strokeWidth="2"
                                    strokeDasharray="5,5"
                                    pointerEvents="none"
                                />
                                {/* Draggable points */}
                                {getScaledPoints().map((pt, idx) => (
                                    <circle
                                        key={idx}
                                        cx={pt.x}
                                        cy={pt.y}
                                        r={12} // Larger for easier interaction
                                        fill={activePointIndex === idx ? "#22c55e" : "#16a34a"}
                                        stroke="#fff"
                                        strokeWidth="2"
                                        className="cursor-move"
                                        style={{
                                            cursor: "move",
                                            pointerEvents: "auto",
                                        }}
                                        onMouseDown={handlePointMouseDown(idx)}
                                    />
                                ))}
                            </svg>
                        )}

                        {/* Camera Label */}
                        <div className="absolute top-4 left-4 bg-black bg-opacity-60 text-white px-3 py-1 rounded-full text-sm font-medium">
                            {cameraDevices.find((cam) => cam.deviceId === selectedCameraId)?.label || "(Unknown Camera)"}
                        </div>
                    </div>
                ) : (
                    <img src={selectedImage} alt="Captured" className="mx-auto max-h-none rounded-xl" />
                )}
                <canvas ref={canvasRef} className="hidden" />
            </div>

            {/* Action Buttons */}
            <div className="mt-6 flex justify-center gap-4">
                {!selectedImage && showGreenbox && (
                    <button
                        onClick={resetPoints}
                        className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
                    >
                        Reset Detection Area
                    </button>
                )}

                <button
                    onClick={selectedImage ? handleReset : capturePhoto}
                    className={`px-6 py-3 border rounded-lg transition ${selectedImage
                        ? "border-gray-300 text-gray-700 hover:bg-gray-50"
                        : "border-blue-500 bg-blue-500 text-white hover:bg-blue-600"
                        }`}
                >
                    {selectedImage ? "Retake" : "Capture Photo"}
                </button>
            </div>
        </div>
    );
};

export default WebcamTab;
